"use client";

import React from "react";
import styled from "styled-components";

import Flex from "../Flex";
import LinkWrapper from "../LinkWrapper";

import { positioningCSS, flexCSS, getColor, transitionCSS, ellipsis } from "../utils";
import { ElementFlexProps, ElementPositioningProps, VariableComponent } from "../ComponentProps";

type Props = {
  text: string;
  subtext?: string;
  selected?: boolean;
  disabled?: boolean;
  link?: string;
  onClick?: (e: React.MouseEvent) => void;
};

type ItemProps = Pick<Props, "selected" | "disabled"> &
  VariableComponent<ElementPositioningProps> &
  VariableComponent<ElementFlexProps>;

const ItemWrapper = styled.button<ItemProps>`
  background: none;
  padding: 0;
  opacity: ${props => (props.disabled ? ".5" : "1")};
  user-select: none;
  box-shadow: none;
  flex-shrink: 0;
  border: none;
  height: 100%;
  flex: 1;
  pointer-events: ${props => (props.disabled ? "none" : null)};
  ${ellipsis};
  ${positioningCSS}
  ${flexCSS}
  ${transitionCSS({
    transitionProperty: "background, opacity, color, text-decoration, box-shadow",
    transitionDuration: "0.5s",
    transitionTimingFunction: "ease",
  })}

  color: ${props =>
    props.selected ? getColor(props.theme.primaryColor, "primary") : getColor("black")};

  span.subtext {
    color: ${getColor("grayDarker")};
    ${positioningCSS({ ml: "xs" })};
  }

  &:hover {
    color: ${props => (props.selected ? getColor(props.theme.primaryColor, "primary") : "black")};
    text-decoration: underline;
  }

  border-bottom: 2px solid ${props => (props.selected ? getColor("grayLight") : "transparent")};

  ${transitionCSS({
    transitionProperty: "color, border-bottom",
  })}

  &:hover,
  &:focus {
    border-bottom: 2px solid
      ${props =>
        props.selected ? getColor(props.theme.primaryColor, "primary") : getColor("grayDark")};
    text-decoration: none;
    outline: none;
  }
  ${props =>
    props.selected &&
    `
  border-bottom: 2px solid ${getColor(props.theme.primaryColor, "primary")};
`}
`;

const Item: React.FC<Props> = ({
  text,
  subtext = null,
  disabled = false,
  link = null,
  ...rest
}) => {
  const linkProps = {
    disabled,
    link,
    height: "100%",
    style: null,
  };
  const buttonProps = {
    disabled,
    ...rest,
    px: "m",
  };
  function renderContent() {
    return (
      <Flex justifyContent="center" alignItems="center">
        {text && <span>{text}</span>}
        {subtext && <span className="subtext">{subtext}</span>}
      </Flex>
    );
  }

  return (
    <LinkWrapper {...linkProps}>
      <ItemWrapper {...buttonProps}>{renderContent()}</ItemWrapper>
    </LinkWrapper>
  );
};

export default Item;
